import { GraphQLResult, generateClient } from "aws-amplify/api";
import { FormInstanceForResolver, FormTemplateForResolver } from "db/definitions";
import { createFormInstanceForBatchResolver, getFormInstanceStuffByFormGroup, getFormInstancesByFormGroup } from "db/queries/formInstanceQueries";
// @ts-ignore
import { formInstancesByFormGroup, formInstancesWithFormTemplatesByFormGroup } from "graphql/queries";
import { recordSentry } from "utils/recordSentry";

/**
 * Given the formGroup id and whether or not we're online, retrieves the formInstances and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formGroup id we want to fetch the formInstances by
 * @param online Whether or not the user is online
 * @returns The array of formInstances retrieved
 */
export async function batchGetFormInstancesByFormGroup(id: string, online: boolean): Promise<FormInstanceForResolver[]> {
	interface QueryResponse {
		formInstancesByFormGroup: {
			items: FormInstanceForResolver[];
			nextToken: string | null;
		};
	}
	const formInstances: FormInstanceForResolver[] = [];
	if (online) {
		try {
			let token: string | null | undefined = undefined;
			while (token !== null) {
				const client = generateClient({ authMode: "userPool" });
				const result = (await client.graphql({
					query: formInstancesByFormGroup,
					authMode: "userPool",
					variables: {
						formGroup: id,
						nextToken: token ?? null,
					},
				})) as GraphQLResult<QueryResponse>;
				const formInstancesFromResponse = result.data.formInstancesByFormGroup.items;
				for (const form of formInstancesFromResponse) {
					formInstances.push(form);
				}
				token = result.data.formInstancesByFormGroup.nextToken;
			}
			return formInstances;
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FORM INSTANCES` });
		}
	} else {
		const offlineFormInstances = getFormInstancesByFormGroup(id);
		return offlineFormInstances;
	}
	return formInstances;
}

export interface FormInstanceWithFormTemplate extends FormInstanceForResolver {
	formTemplate: FormTemplateForResolver;
}

export interface FormInstanceAndFormTemplate {
	formInstances: FormInstanceForResolver[];
	formTemplates: FormTemplateForResolver[];
}

/**
 * Given the formGroup id and whether or not we're online, retrieves the formInstances (with formTemplates) and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formGroup id we want to fetch the formInstances by
 * @param online Whether or not we're online
 * @returns The array of formInstances retrieved
 */
export async function batchGetFormInstancesAndFormTemplateByFormGroup(id: string, online: boolean): Promise<FormInstanceAndFormTemplate> {
	interface QueryResponse {
		formInstancesByFormGroup: {
			items: FormInstanceWithFormTemplate[];
			nextToken?: string;
		};
	}
	if (id) {
		try {
			if (online) {
				let token: string | null | undefined = undefined;
				const formInstances: FormInstanceForResolver[] = [];
				const formTemplates: FormTemplateForResolver[] = [];
				while (token !== null) {
					const client = generateClient({ authMode: "userPool" });
					const result = (await client.graphql({
						query: formInstancesWithFormTemplatesByFormGroup,
						authMode: "userPool",
						variables: {
							formGroup: id,
							nextToken: token ?? null,
						},
					})) as unknown as GraphQLResult<QueryResponse>;
					const formInstancesFromResponse = result.data.formInstancesByFormGroup.items;
					for (const form of formInstancesFromResponse) {
						// This will have duplicates, but I made this bad boy, and I de-dupoe later because this is only 1 of n formGroups, so would need to dedupe later even if I dedupe here
						formTemplates.push(form.formTemplate);
						const parsedForm = createFormInstanceForBatchResolver(form);
						formInstances.push(parsedForm);
					}
					token = result.data.formInstancesByFormGroup.nextToken || null;
				}
				return { formInstances, formTemplates };
			} else {
				const formInstanceAndFormTemplate = await getFormInstanceStuffByFormGroup(id);
				return formInstanceAndFormTemplate;
			}
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FORM INSTANCES ${online ? "ONLINE" : "OFFLINE"}` });
		}
	}
	return { formInstances: [], formTemplates: [] };
}
